import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import type { PaletteMode } from '@mui/material';


export const HUB_MODE_STORAGE_KEY = 'hubThemeMode';

export const getStoredHubMode = (): PaletteMode =>
  (window.localStorage.getItem(HUB_MODE_STORAGE_KEY) === 'dark' ? 'dark' : 'light');

const ORANGE = '#f75f01';

interface ModeTokens {
  background: { default: string; paper: string };
  text: { primary: string; secondary: string };
  divider: string;
  paperBorder: string;
  paperShadow: string;
  successLight: string;
}

const lightTokens: ModeTokens = {
  background: { default: '#fbfaf8', paper: '#ffffff' },
  text: { primary: '#1a1a1a', secondary: '#6f6b66' },
  divider: '#e8e6e2',
  paperBorder: '#e8e6e2',
  paperShadow: '0 1px 2px rgba(20,16,12,.04), 0 4px 14px rgba(20,16,12,.05)',
  successLight: '#eaf5ef',
};

const darkTokens: ModeTokens = {
  background: { default: '#17140f', paper: '#211d18' }, // warm, not pure black
  text: { primary: '#f6f4f0', secondary: '#a8a29a' },
  divider: '#332f28',
  paperBorder: '#332f28',
  paperShadow: '0 1px 2px rgba(0,0,0,.32), 0 4px 14px rgba(0,0,0,.38)',
  successLight: '#1e3a2b',
};

export const createHubTheme = (mode: PaletteMode = 'light') => {
  const tokens = mode === 'dark' ? darkTokens : lightTokens;

  const theme = createTheme({
    palette: {
      mode,
      primary: {
        main: ORANGE,
        light: '#ff8440',
        dark: '#d24f00',
        contrastText: '#ffffff',
      },
      success: { main: '#2f8a5b', light: tokens.successLight, contrastText: '#ffffff' },
      background: tokens.background,
      text: tokens.text,
      divider: tokens.divider,
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: '-apple-system, BlinkMacSystemFont, \'Inter\', \'Segoe UI\', sans-serif',
      h1: { fontWeight: 800, letterSpacing: '-0.02em' },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 700 },
      button: { textTransform: 'none', fontWeight: 600 },
      overline: { fontWeight: 700, letterSpacing: '0.12em' },
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${tokens.paperBorder}`,
            boxShadow: tokens.paperShadow,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: { root: { borderRadius: 999 } },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            '&.MuiInputBase-readOnly': {
              backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(20,16,12,0.04)',
            },
            '&.MuiInputBase-readOnly .MuiInputBase-input': {
              cursor: 'default',
            },
            '&.MuiInputBase-readOnly .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.divider,
            },
            '&.MuiInputBase-readOnly.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.divider,
              borderWidth: 1,
            },
          },
        },
      },
    },
  });

  return responsiveFontSizes(theme);
};

export default createHubTheme('light');
